import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import StorageService from './StorageService';
import { getImageTextureObj } from 'support/generalUtils';

type ConstructorArgs = {
  deviceIntegration: AbstractDeviceIntegration;
  storageService: StorageService;
};

export default class OfflineErrorService {
  private readonly deviceIntegration: AbstractDeviceIntegration;
  private readonly storageService;
  private onOfflineActions: Record<string, () => void> = {};
  private onOnlineActions: Record<string, () => void> = {};
  private offlineImage = getImageTextureObj(
    'static/images/offline-cloud.svg',
    36,
    36,
  );

  constructor({ deviceIntegration, storageService }: ConstructorArgs) {
    this.deviceIntegration = deviceIntegration;
    this.storageService = storageService;

    this.clearOfflineError();
    this.deviceIntegration.handleNetworkChange(this.networkChanged.bind(this));
  }

  private networkChanged(isOnline: boolean) {
    if (isOnline) {
      this.clearOfflineError();

      let actions: Array<() => void> = [];
      if (document.visibilityState === 'visible') {
        actions = Object.values(this.onOnlineActions);
      }

      actions.forEach(action => action());
    } else {
      this.storageService.offlineError.set(true);
      const actions: Array<() => void> = Object.values(this.onOfflineActions);
      actions.forEach(action => action());
    }
  }

  private clearOfflineError() {
    if (this.storageService.offlineError.get()) {
      this.storageService.offlineError.remove();
    }
  }

  loadImage() {
    return this.offlineImage;
  }

  isOffline() {
    return this.storageService.offlineError.get() ?? false;
  }

  addOfflineAction(key: string, action: () => void) {
    if (this.onOfflineActions[key]) delete this.onOfflineActions[key];

    this.onOfflineActions[key] = action;
  }

  resumeOnlineAction(key: string, action: () => void) {
    if (this.onOnlineActions[key]) delete this.onOnlineActions[key];

    this.onOnlineActions[key] = action;
  }
}
